/**
 * The tenant onboarding requests (plan 2026-10-16-customer-onboarding, WS-16, rulings R-055 and
 * R-072).
 *
 * One onboarding request makes a tenant, its cloud base, its dedicated data plane, its licences and
 * its org seed in one job. provisioning-svc owns the job. The gateway sends `/v1/admin` to
 * site-admin-api, and site-admin-api forwards these four routes to provisioning-svc with the
 * caller's token (R-072 item 3, workstream WS-17). The wire shapes are the contract of
 * `@zion/sdk-contracts` (`onboarding/`); this module restates none of them.
 *
 * | # | endpoint | contract | permission, kind |
 * |---|---|---|---|
 * | O1 | `POST /v1/admin/onboarding-requests` | body `onboardingRequestSchema`; answer 201 `onboardingRequestViewSchema` | `cp:dp:create` and `cp:tenant:create`, and `cp:billing:write` when `apps` is not empty; site |
 * | O2 | `GET /v1/admin/onboarding-requests/:id` | answer `onboardingRequestViewSchema` | `cp:dp:read`, site |
 * | O3 | `POST /v1/admin/onboarding-requests/:id/retry` | no body; answer 200 `onboardingRequestViewSchema` | `cp:dp:update`, site |
 * | O4 | `PUT /v1/admin/onboarding-requests/:id/client-secret` | body `onboardingClientSecretBodySchema`; answer 204 with no body | `cp:dp:update`, site |
 *
 * A coded refusal is a 400 or 409 with the body `{ code, message }` (`onboardingRefusalSchema`);
 * `onboardingRefusalOf` in `platform/errors.ts` reads it. A missing permission is a 403, an unknown
 * id a 404, and a refused secret hold at intake a 503.
 *
 * The client secret (O1 `signIn.clientSecret`, O4 `clientSecret`) leaves the browser in the request
 * body and nowhere else. No function here logs a body, and no answer carries the secret (D12).
 *
 * Every path literal starts with `/v1/` and names its id `requestId`, so the dev fixture gate
 * `fixture-routes-answer.test.ts` fills each with a fixture id and probes it.
 *
 * Three more routes serve the tenant owner invitation and the data plane mail fact (plan
 * 2026-10-24-onboarding-mail-off-and-tenant-iam, WS-4; `00-index.md` contract 2; D-1, D-4, D-5).
 * provisioning-svc owns them (WS-2), and site-admin-api proxies them under `/v1/admin` (WS-3):
 *
 * | # | endpoint | answer | permission, kind |
 * |---|---|---|---|
 * | O5 | `GET /v1/admin/onboarding-requests/:id/owner-invitation` | `{ state, email, expiresAt, mailEnabled }` | `cp:dp:read`, site |
 * | O6 | `POST /v1/admin/onboarding-requests/:id/owner-invitation/reissue` | no body; answer 200 `{ acceptUrl, expiresAt }`, `Cache-Control: no-store` | `cp:dp:update`, site |
 * | O7 | `GET /v1/admin/onboarding/mail-status` | `{ dpMailEnabled, transport }` | `cp:dp:read`, site (R-007) |
 *
 * No SDK change (defaults row 2, D-3): the shapes are a copy of contract 2, as `OwnerInvitationView`
 * and the two schemas of provisioning-svc's `owner-invitation-contract.ts` and `DpMailStatus` of its
 * `mail-status.ts` declare it. This package has no `zod`, so each answer goes through a strict
 * hand-written reader built on `wire-shape.ts`: a key outside the contract, a missing key or a value
 * outside its rule rejects with a fixed message that holds no part of the body. A change to a shape is
 * a change to `00-index.md` first, and to all three copies.
 *
 * The accept link of O6 carries a bearer token (D-2). No function here logs it, stores it or puts any
 * part of it in an error message. The caller shows it once (`OneTimeSecretDialog`) and drops it.
 */
import {
  onboardingRequestViewSchema,
  type OnboardingClientSecretBody,
  type OnboardingRequest,
  type OnboardingRequestView,
} from '@zion/sdk-contracts';
import { api, apiFetch } from '../platform/http/client';
import { isRecord } from './wire-shape';

/**
 * The route permission of O1, site-wide: the `@RequirePermission` marker of the provisioning-svc
 * create route. `useHasPermission(ONBOARDING_CREATE_PERMISSION)` takes no resource id.
 */
export const ONBOARDING_CREATE_PERMISSION = 'cp:dp:create';

/** The second permission of O1, site-wide: the job makes a tenant, so the caller must hold the tenant create. */
export const ONBOARDING_TENANT_PERMISSION = 'cp:tenant:create';

/**
 * The third permission of O1, site-wide, and only for a request that names one app or more: the job
 * grants the licences (R-016 of the plan). Without it the wizard offers no app.
 */
export const ONBOARDING_LICENCE_PERMISSION = 'cp:billing:write';

/** The permission of O2, site-wide. The progress view needs it. */
export const ONBOARDING_READ_PERMISSION = 'cp:dp:read';

/** The permission of O3 and O4, site-wide: the Retry button and the client secret paste need it. */
export const ONBOARDING_UPDATE_PERMISSION = 'cp:dp:update';

/** The collection path of O1. */
export const ONBOARDING_REQUESTS_PATH = '/v1/admin/onboarding-requests';

/** The path of one request (O2): `/v1/admin/onboarding-requests/<requestId>`. The id is encoded. */
export function onboardingRequestPath(requestId: string): string {
  return `/v1/admin/onboarding-requests/${encodeURIComponent(requestId)}`;
}

/** The retry door of one request (O3): `/v1/admin/onboarding-requests/<requestId>/retry`. The id is encoded. */
export function onboardingRetryPath(requestId: string): string {
  return `/v1/admin/onboarding-requests/${encodeURIComponent(requestId)}/retry`;
}

/** The client secret door of one request (O4): `/v1/admin/onboarding-requests/<requestId>/client-secret`. The id is encoded. */
export function onboardingClientSecretPath(requestId: string): string {
  return `/v1/admin/onboarding-requests/${encodeURIComponent(requestId)}/client-secret`;
}

/**
 * O1. Send one onboarding request. The job starts after the answer, in `pending`.
 *
 * Caller: the wizard of `OnboardTenantPane`, with a body that it built from its draft. The answer
 * must parse with `onboardingRequestViewSchema`; a body that does not parse rejects with the
 * `ZodError`, so a producer drift shows as a failed send and never as a wrong view (default F3).
 * Rejects with the client's `ApiError`: 400 for a body the strict pipe refuses or a 400 refusal code;
 * 409 `TENANT_SLUG_TAKEN` or `DATA_PLANE_NAME_TAKEN`; 403 for a missing permission; 503 when the CP
 * could not hold the client secret (nothing was stored).
 */
export async function createOnboardingRequest(body: OnboardingRequest): Promise<OnboardingRequestView> {
  const answer = await api.post<unknown>(ONBOARDING_REQUESTS_PATH, body);
  return onboardingRequestViewSchema.parse(answer);
}

/**
 * The message of an O2 or O3 answer that parses but names another request than the one the caller
 * asked for. The console never shows or writes to such a view: a write keyed by its id would land
 * on the other request. The message holds no part of the body.
 */
export const ONBOARDING_VIEW_WRONG_ID_MESSAGE = 'The control plane answered with another onboarding request than the one this page asked for.';

/*
 * Parse an O2 or O3 answer and hold it to the asked id. A UUID compares without case, as RFC 9562
 * says, because the URL can carry upper-case hex and the CP answers lower-case.
 */
function viewOfRequest(requestId: string, answer: unknown): OnboardingRequestView {
  const view = onboardingRequestViewSchema.parse(answer);
  if (view.id.toLowerCase() !== requestId.toLowerCase()) throw new Error(ONBOARDING_VIEW_WRONG_ID_MESSAGE);
  return view;
}

/**
 * O2. Read the view of one request: its stored form, with no secret, and the progress of its job.
 *
 * Caller: `useOnboardingRequest`, on mount, on each poll tick and on each reload. The answer must
 * parse with `onboardingRequestViewSchema`, else the read rejects with the `ZodError`. A parsed
 * answer whose `id` is not `requestId` (case aside) rejects with `Error`
 * {@link ONBOARDING_VIEW_WRONG_ID_MESSAGE}. Rejects with the client's `ApiError`: 400 for an id that
 * is not a UUID, 403 without `cp:dp:read`, 404 for an unknown id. `init.signal`, when given, aborts
 * the read: the call then rejects with the abort reason, as `fetch` does. No side effect.
 */
export async function getOnboardingRequest(requestId: string, init: Pick<RequestInit, 'signal'> = {}): Promise<OnboardingRequestView> {
  const answer = await apiFetch<unknown>(onboardingRequestPath(requestId), init);
  return viewOfRequest(requestId, answer);
}

/**
 * O3. Move a failed request back to the step it failed in, and start its job again.
 *
 * Caller: the Retry button of `OnboardProgress`. The answer must parse with
 * `onboardingRequestViewSchema`, and its `id` must be `requestId` (case aside), else the call
 * rejects with `Error` {@link ONBOARDING_VIEW_WRONG_ID_MESSAGE}. Rejects with the client's
 * `ApiError`: 409 `REQUEST_NOT_RETRYABLE` for a request that is not failed, 403 without
 * `cp:dp:update`, 404 for an unknown id.
 */
export async function retryOnboardingRequest(requestId: string): Promise<OnboardingRequestView> {
  const answer = await api.post<unknown>(onboardingRetryPath(requestId));
  return viewOfRequest(requestId, answer);
}

/**
 * O4. Send the client secret of a request whose sign-in type takes one (R-055 item 3).
 *
 * Caller: `OnboardSecretPaste`, with `{ clientSecret }`. Resolves `undefined` on the 204. A second
 * send before the org seed replaces the first. Rejects with the client's `ApiError`: 409
 * `CLIENT_SECRET_NOT_ACCEPTED` when the job is past the point where a secret can reach the org seed,
 * or the sign-in type takes no secret; 400 for a body the strict pipe refuses; 403 without
 * `cp:dp:update`; 404 for an unknown id. The secret is in the body only.
 */
export async function setOnboardingClientSecret(requestId: string, body: OnboardingClientSecretBody): Promise<void> {
  await api.put<undefined>(onboardingClientSecretPath(requestId), body);
}

/**
 * The six states of O5 (D-4; R-009): the five a data plane reports and `unavailable`, which the CP
 * adds when the data plane gives no readable answer.
 * - `pending_mail_off`: a pending invitation that has not expired, on a data plane whose mail does not send;
 * - `pending_mail_on`: a pending invitation that has not expired, on a data plane whose mail sends;
 * - `accepted`: the owner accepted the invitation;
 * - `expired`: a pending invitation whose `expiresAt` has passed;
 * - `none`: the org holds no seed-owner invitation (R-002: also a revoked newest row);
 * - `unavailable`: the data plane did not answer, with `email` and `expiresAt` `null` and
 *   `mailEnabled` `false` (R-003, R-009).
 */
export const OWNER_INVITATION_STATES = ['pending_mail_off', 'pending_mail_on', 'accepted', 'expired', 'none', 'unavailable'] as const;

/** One state of {@link OWNER_INVITATION_STATES}. */
export type OwnerInvitationState = (typeof OWNER_INVITATION_STATES)[number];

/**
 * The answer of O5: the owner invitation of a request's org, as its data plane reports it.
 * `email` is the invited address or `null`; `expiresAt` is an ISO 8601 UTC time in the
 * `toISOString()` form or `null`; `mailEnabled` is the data plane's live mail switch, `false` for
 * `unavailable` (R-009).
 */
export interface OwnerInvitationView {
  readonly state: OwnerInvitationState;
  readonly email: string | null;
  readonly expiresAt: string | null;
  readonly mailEnabled: boolean;
}

/**
 * The answer of O6: the new accept link and its expiry. `acceptUrl` has the one form
 * {@link OWNER_INVITATION_ACCEPT_URL_PATTERN} admits and carries a bearer token; it shows once.
 */
export interface OwnerInvitationLink {
  readonly acceptUrl: string;
  readonly expiresAt: string;
}

/** The data plane mail transports of O7, the three values the chart renders (`dpMail.transport`). */
export const DP_MAIL_TRANSPORTS = ['smtp', 'sendgrid', 'none'] as const;

/** One transport of {@link DP_MAIL_TRANSPORTS}. */
export type DpMailTransport = (typeof DP_MAIL_TRANSPORTS)[number];

/**
 * The answer of O7: the data plane mail fact of this CP (D-5). `dpMailEnabled` is `true` only when the
 * transport is not `none` and the send switch is on. One fact for every data plane this CP makes.
 */
export interface OnboardingMailStatus {
  readonly dpMailEnabled: boolean;
  readonly transport: DpMailTransport;
}

/** The longest owner address O5 relays, in characters, as provisioning-svc bounds it. */
export const OWNER_INVITATION_EMAIL_MAX_LENGTH = 254;

/** The longest accept link O6 relays, in characters, as provisioning-svc bounds it. */
export const OWNER_INVITATION_ACCEPT_URL_MAX_LENGTH = 2_048;

/**
 * The one expiry form both answers carry: `Date.prototype.toISOString()`, nothing before or after.
 * The pattern has no `m` flag, so its `$` in JavaScript matches at the end of the input alone: a
 * final line feed, carriage return or line separator fails the match. The pattern admits a day or an
 * hour that no calendar holds (`2026-02-30`, `T24:00`), which `Date.parse` rolls into another
 * instant, so the readers also require the text to equal the `toISOString()` of its own instant.
 */
export const OWNER_INVITATION_EXPIRES_AT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/**
 * The one accept link form O6 carries: `https://<host>/invitations/accept?token=<token>`, with a
 * lower-case DNS host of two or more labels, no user part, no port and a base64url token. The copy
 * of `OWNER_INVITATION_ACCEPT_URL_PATTERN` in provisioning-svc. The pattern has no `m` flag, so its
 * `$` in JavaScript matches at the end of the input alone: a final line feed, carriage return or
 * line separator fails the match. The refusal cases of `onboarding.api.test.ts` pin each of them.
 */
export const OWNER_INVITATION_ACCEPT_URL_PATTERN =
  /^https:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\/invitations\/accept\?token=[A-Za-z0-9_-]+$/;

/**
 * The longest an O6 reissue runs before the browser stops it, in milliseconds: 45 s, the bound of
 * the provisioning-job remedies (`REMEDY_TIMEOUT_MS`), above the gateway's 30 s route timeout. The
 * reissue confirm of `OnboardOwnerInvitation` cannot close while its write runs, so a reissue that
 * never settles would keep the operator in the dialog until a page reload.
 */
export const OWNER_INVITATION_REISSUE_TIMEOUT_MS = 45_000;

/**
 * The rejection of an O6 reissue that ran out of {@link OWNER_INVITATION_REISSUE_TIMEOUT_MS}. Its
 * `message` is the sentence the card shows. The data plane may have revoked the old link and made a
 * new one that never reached the browser, so the outcome is unknown: the card reads the state again.
 * The message holds no part of any link.
 */
export class OwnerInvitationReissueTimeoutError extends Error {
  constructor() {
    super(
      `The reissue did not finish in ${OWNER_INVITATION_REISSUE_TIMEOUT_MS / 1000} seconds. The data plane may have revoked the old link: read the state, then reissue again.`,
    );
    this.name = 'OwnerInvitationReissueTimeoutError';
  }
}

/** The message an O5 answer outside the contract earns. The body is never echoed. */
export const OWNER_INVITATION_WRONG_SHAPE_MESSAGE = 'The control plane answered with an owner invitation state this console cannot read.';

/** The message an O6 answer outside the contract earns. It holds no part of the body, so no token. */
export const OWNER_INVITATION_LINK_WRONG_SHAPE_MESSAGE =
  'The control plane answered the reissue with a link this console cannot read. The data plane may have revoked the old link: read the state, then reissue again.';

/** The message an O7 answer outside the contract earns. The body is never echoed. */
export const MAIL_STATUS_WRONG_SHAPE_MESSAGE = 'The control plane answered with a mail status this console cannot read.';

/** The permission of O7, site-wide (R-007): the read permission of the data plane surfaces. */
export const ONBOARDING_MAIL_STATUS_PERMISSION = 'cp:dp:read';

/** The path of O7. */
export const ONBOARDING_MAIL_STATUS_PATH = '/v1/admin/onboarding/mail-status';

/** The owner invitation of one request (O5): `/v1/admin/onboarding-requests/<requestId>/owner-invitation`. The id is encoded. */
export function onboardingOwnerInvitationPath(requestId: string): string {
  return `/v1/admin/onboarding-requests/${encodeURIComponent(requestId)}/owner-invitation`;
}

/** The reissue door of one request (O6): `/v1/admin/onboarding-requests/<requestId>/owner-invitation/reissue`. The id is encoded. */
export function onboardingOwnerInvitationReissuePath(requestId: string): string {
  return `/v1/admin/onboarding-requests/${encodeURIComponent(requestId)}/owner-invitation/reissue`;
}

/* True when the object holds exactly the keys named, no more and no fewer. */
function hasExactKeys(body: Record<string, unknown>, keys: readonly string[]): boolean {
  const own = Object.keys(body);
  return own.length === keys.length && keys.every((key) => Object.hasOwn(body, key));
}

/*
 * True for an expiry in the `toISOString()` form that names a real instant: the text is the
 * `toISOString()` of the instant it parses to, so a rolled-over day or hour fails.
 */
function isExpiry(value: unknown): value is string {
  if (typeof value !== 'string' || !OWNER_INVITATION_EXPIRES_AT_PATTERN.test(value)) return false;
  const instant = Date.parse(value);
  return !Number.isNaN(instant) && new Date(instant).toISOString() === value;
}

/* True for `null` and for an address of 1 to 254 characters. */
function isOwnerEmail(value: unknown): value is string | null {
  return value === null || (typeof value === 'string' && value.length >= 1 && value.length <= OWNER_INVITATION_EMAIL_MAX_LENGTH);
}

/* True when `value` is one member of `members`. */
function isMember<T extends string>(members: readonly T[], value: unknown): value is T {
  return members.some((member) => member === value);
}

/**
 * Read an O5 answer. Input: any value. Output: a new {@link OwnerInvitationView} for an object with
 * exactly `state`, `email`, `expiresAt` and `mailEnabled`, where `state` is one of
 * {@link OWNER_INVITATION_STATES}, `email` is `null` or a string of 1 to 254 characters, `expiresAt` is
 * `null` or an instant in {@link OWNER_INVITATION_EXPIRES_AT_PATTERN} form that equals the
 * `toISOString()` of its own instant, and `mailEnabled` is a boolean. Throws `Error` with
 * {@link OWNER_INVITATION_WRONG_SHAPE_MESSAGE} for any other value. Pure.
 */
export function parseOwnerInvitationView(body: unknown): OwnerInvitationView {
  if (!isRecord(body) || !hasExactKeys(body, ['state', 'email', 'expiresAt', 'mailEnabled'])) throw new Error(OWNER_INVITATION_WRONG_SHAPE_MESSAGE);
  const { state, email, expiresAt, mailEnabled } = body;
  if (!isMember(OWNER_INVITATION_STATES, state) || !isOwnerEmail(email) || !(expiresAt === null || isExpiry(expiresAt)) || typeof mailEnabled !== 'boolean') {
    throw new Error(OWNER_INVITATION_WRONG_SHAPE_MESSAGE);
  }
  return { state, email, expiresAt, mailEnabled };
}

/**
 * Read an O6 answer. Input: any value. Output: a new {@link OwnerInvitationLink} for an object with
 * exactly `acceptUrl` and `expiresAt`, where `acceptUrl` is at most 2,048 characters in
 * {@link OWNER_INVITATION_ACCEPT_URL_PATTERN} form (an `https:` link and no other scheme) and
 * `expiresAt` is an instant in {@link OWNER_INVITATION_EXPIRES_AT_PATTERN} form that equals the
 * `toISOString()` of its own instant. Throws `Error` with
 * {@link OWNER_INVITATION_LINK_WRONG_SHAPE_MESSAGE} for any other value; the message holds no part of
 * the body, so no token reaches an error text (defaults row 5). Pure.
 */
export function parseOwnerInvitationLink(body: unknown): OwnerInvitationLink {
  if (!isRecord(body) || !hasExactKeys(body, ['acceptUrl', 'expiresAt'])) throw new Error(OWNER_INVITATION_LINK_WRONG_SHAPE_MESSAGE);
  const { acceptUrl, expiresAt } = body;
  const urlOk = typeof acceptUrl === 'string' && acceptUrl.length <= OWNER_INVITATION_ACCEPT_URL_MAX_LENGTH && OWNER_INVITATION_ACCEPT_URL_PATTERN.test(acceptUrl);
  if (!urlOk || !isExpiry(expiresAt)) throw new Error(OWNER_INVITATION_LINK_WRONG_SHAPE_MESSAGE);
  return { acceptUrl, expiresAt };
}

/**
 * Read an O7 answer. Input: any value. Output: a new {@link OnboardingMailStatus} for an object with
 * exactly `dpMailEnabled`, a boolean, and `transport`, one of {@link DP_MAIL_TRANSPORTS}. Throws
 * `Error` with {@link MAIL_STATUS_WRONG_SHAPE_MESSAGE} for any other value. The transport is the
 * narrower enum of the producer, not contract 2's wider `string` (defaults row 1). Pure.
 */
export function parseOnboardingMailStatus(body: unknown): OnboardingMailStatus {
  if (!isRecord(body) || !hasExactKeys(body, ['dpMailEnabled', 'transport'])) throw new Error(MAIL_STATUS_WRONG_SHAPE_MESSAGE);
  const { dpMailEnabled, transport } = body;
  if (typeof dpMailEnabled !== 'boolean' || !isMember(DP_MAIL_TRANSPORTS, transport)) throw new Error(MAIL_STATUS_WRONG_SHAPE_MESSAGE);
  return { dpMailEnabled, transport };
}

/**
 * O5. Read the owner invitation of one request's org from its data plane.
 *
 * Caller: `OnboardOwnerInvitation`, for a `ready` request, on mount, on Read again and after each
 * reissue. Resolves the parsed view; `unavailable` is an answer, not an error. Rejects with the
 * client's `ApiError`: 400 for an id that is not a UUID, 403 without `cp:dp:read`, 404 for an unknown
 * id, 409 for a request whose job has made no org yet (R-008). Rejects with `Error`
 * {@link OWNER_INVITATION_WRONG_SHAPE_MESSAGE} for a 2xx body outside the contract. `init.signal`,
 * when given, aborts the read. No side effect.
 */
export async function getOwnerInvitation(requestId: string, init: Pick<RequestInit, 'signal'> = {}): Promise<OwnerInvitationView> {
  const answer = await apiFetch<unknown>(onboardingOwnerInvitationPath(requestId), init);
  return parseOwnerInvitationView(answer);
}

/**
 * O6. Reissue the owner invitation of a `ready` request and get its new accept link, once.
 *
 * Caller: the reissue confirm of `OnboardOwnerInvitation`. The data plane revokes the old link and
 * sends no mail (R-001); the CP writes the audit row `invitation_accept_url_revealed`. Resolves the
 * parsed link. Rejects with the client's `ApiError`, whose body is the CP's `{ statusCode, message,
 * error }` with no link: 403 without `cp:dp:update`; 404 for an unknown id; 409 for a request that is
 * not `ready`, with no org, or whose data plane holds nothing to reissue; 422, 424, 501 or 502 for a
 * data plane fault; 500 for a CP with no data plane apex (R-016). Rejects with `Error`
 * {@link OWNER_INVITATION_LINK_WRONG_SHAPE_MESSAGE} for a 2xx body outside the contract. Rejects
 * with {@link OwnerInvitationReissueTimeoutError} when the POST, a token refresh after a 401
 * included, has not settled in {@link OWNER_INVITATION_REISSUE_TIMEOUT_MS}: the signal aborts the
 * fetch, and its abort also rejects the race, by the mechanism of `postRemedy` in
 * `provisioningJobs.api.ts`. Any other failure rejects unchanged, so a 409 keeps the CP's sentence.
 * The abort listener goes when the call settles.
 *
 * Side effects: one POST, which revokes the org's old accept link on the data plane. The function
 * keeps no copy of the link.
 */
export async function reissueOwnerInvitation(requestId: string): Promise<OwnerInvitationLink> {
  const signal = AbortSignal.timeout(OWNER_INVITATION_REISSUE_TIMEOUT_MS);
  const settled = new AbortController();
  const bound = new Promise<never>((_, reject) => {
    signal.addEventListener('abort', () => reject(new OwnerInvitationReissueTimeoutError()), { once: true, signal: settled.signal });
  });
  let answer: unknown;
  try {
    answer = await Promise.race([api.post<unknown>(onboardingOwnerInvitationReissuePath(requestId), undefined, { signal }), bound]);
  } catch (error) {
    if (signal.aborted) throw new OwnerInvitationReissueTimeoutError();
    throw error;
  } finally {
    settled.abort();
  }
  return parseOwnerInvitationLink(answer);
}

/**
 * O7. Read whether the data planes of this CP send mail (D-5).
 *
 * Caller: the wizard of `OnboardTenantPane`, once per mount, for the warning of `ReviewStep`.
 * Resolves the parsed fact. Rejects with the client's `ApiError`: 403 without `cp:dp:read`. Rejects
 * with `Error` {@link MAIL_STATUS_WRONG_SHAPE_MESSAGE} for a 2xx body outside the contract. No side
 * effect.
 */
export async function getOnboardingMailStatus(): Promise<OnboardingMailStatus> {
  const answer = await api.get<unknown>(ONBOARDING_MAIL_STATUS_PATH);
  return parseOnboardingMailStatus(answer);
}
